<script setup>
import { computed, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { useSession } from '@/stores/session.js'
import { WIDE_TYPES, SPECIAL_FIELDS, CREATE_ONLY, fieldApplies, toInputs, bodyFrom, layoutSections, sectionTitle } from '@/lib/forms.js'
import { ruleRecord, fieldEffects, unmetRequirements, unmetPatterns } from '@/lib/fieldRules.js'
import { saveRecord } from '@/lib/records.js'
import { api } from '@/lib/http.js'
import { entityPath, ENTITIES } from '@/lib/entities.js'
import { useSave } from '@/composables/useSave.js'
import { useDebounce } from '@/composables/useDebounce.js'
import FieldInput from './FieldInput.vue'
import ActivityReminders from './ActivityReminders.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiNotice from '@/components/ui/UiNotice.vue'

/**
 * Create or edit any record. Sections and their order come from the form layout (the
 * tenant's, else the built-in one, D-133), custom fields included; visibility and requirements from field rules evaluated in the browser —
 * the same rules Core applies. Saving goes through the offline queue (online first).
 */
const props = defineProps({
  entity: { type: String, required: true },
  record: { type: Object, default: null },
  defaults: { type: Object, default: () => ({}) },
  compact: Boolean,
  submitLabel: { type: String, default: '' },
  hideActions: Boolean,
  readonly: Boolean,
  // The caller reports success itself (a create panel, with a link to the new record).
  quietSuccess: Boolean,
  // More of the record edited beside the form (an offer's terms, D-152): its changed fields, saved with the form's in one request.
  extend: { type: Function, default: null },
  // Fields printed in the document the client receives (D-152): marked by their label.
  printed: { type: Array, default: () => [] },
})
const emit = defineEmits(['saved', 'cancel', 'dirty', 'conflict'])
const { t } = useI18n()
const meta = useMeta()
const session = useSession()
const { report, fieldErrors } = useSave()

const creating = computed(() => !props.record?.uuid)
const initial = toInputs(props.entity, props.record || {}, meta)
const original = JSON.parse(JSON.stringify(initial.inputs))
const inputs = reactive({ ...JSON.parse(JSON.stringify(initial.inputs)), ...(creating.value ? props.defaults : {}) })
const refs = reactive({ ...initial.refs, ...(props.defaults._refs || {}) })
delete inputs._refs
if (creating.value && !inputs.owner && session.me) inputs.owner = { type: 'user', uuid: session.me.uuid, label: session.me.name }
if (creating.value && props.entity === 'opportunity' && !inputs.pipeline_uuid) {
  const p = meta.defaultPipeline('opportunity')
  inputs.pipeline_uuid = p?.uuid || null
  inputs.stage_uuid = inputs.stage_uuid || p?.stages?.find((s) => s.kind === 'open')?.uuid || null
  inputs.currency_code = inputs.currency_code || meta.baseCurrency
}
if (creating.value && props.entity === 'activity' && !inputs.type) inputs.type = 'task'
// "Notify … before" (D-257): the person's usual reminders to start from, for the doer and for them.
if (creating.value && props.entity === 'activity') {
  if (!Array.isArray(inputs.reminders)) inputs.reminders = [...(session.me?.settings?.reminder_offsets || [15])]
  if (inputs.remind_me === undefined) inputs.remind_me = true
}
// Already over (D-351): made closed — an inbound call usually is (ticked by itself, untickable); when
// it was asked as when it is now; nothing to remind of.
let completedSet = false
if (creating.value && props.entity === 'activity') {
  if (inputs.completed === undefined) inputs.completed = false
  watch(() => [inputs.type, inputs.direction], ([type, direction]) => {
    if (!completedSet && type === 'call' && direction === 'inbound') inputs.completed = true
  }, { immediate: true })
  watch(() => inputs.completed, (done) => {
    if (!done || inputs.due_at || inputs.start_at) return
    inputs[inputs.type === 'meeting' ? 'start_at' : 'due_at'] = new Date().toISOString()
  })
}
const remindable = computed(() => creating.value && props.entity === 'activity' && !inputs.completed && !!(inputs.due_at || inputs.start_at))
const forSomeoneElse = computed(() => !!inputs.owner && (inputs.owner.type === 'group' || inputs.owner.uuid !== session.me?.uuid))
if (creating.value && props.entity === 'offer' && !inputs.language) inputs.language = 'pl'

// Dictation in the form's fields (D-147): saving waits for what is still being written.
const dictations = new Set()
provide('formDictation', dictations)

const errors = ref({})
const saving = ref(false)
const duplicates = ref([])
const duplicateBlocking = ref(false)
const liveDuplicates = ref([])
const formError = ref('')

function set(key, value) {
  // Ticked or unticked by the person: no longer by the call's direction.
  if (key === 'completed') completedSet = true
  inputs[key] = value
  if (errors.value[key]) {
    const next = { ...errors.value }
    delete next[key]
    errors.value = next
  }
}

const changedBody = computed(() => bodyFrom(props.entity, inputs, original, meta, { creating: false }))
const changedCount = computed(() => Object.keys(changedBody.value).length + Math.max(0, Object.keys(changedBody.value.custom_fields || {}).length - 1))
/** Field names (as the form names them) that differ from the saved record. */
const changedNames = computed(() => {
  if (creating.value) return new Set()
  const body = changedBody.value
  const names = new Set(Object.keys(body.custom_fields || {}).map((k) => `cf.${k}`))
  for (const f of meta.fields(props.entity)) if ((f.input || f.name) in body) names.add(f.name)
  for (const k of Object.keys(body)) if (k !== 'custom_fields') names.add(k)
  return names
})
const dirty = computed(() => changedCount.value > 0)
const extended = () => (props.extend ? props.extend() || {} : {})
watch(dirty, (v) => emit('dirty', v))
// Changed by the person, compared with how the form opened (defaults included): what a
// panel asks about before it is closed.
const opened = JSON.stringify(inputs)
const changed = computed(() => JSON.stringify(inputs) !== opened)
/** The form as it stands, to carry into another form (a panel expanded to a full page). */
function snapshot() {
  return { ...JSON.parse(JSON.stringify(inputs)), _refs: JSON.parse(JSON.stringify(refs)) }
}

// ---------------------------------------------------------------- rules

const effects = computed(() => {
  const record = ruleRecord(props.entity, { ...(props.record || {}), ...inputs, owner: inputs.owner }, meta)
  return fieldEffects(meta.fieldRules(props.entity), record, props.record ? ruleRecord(props.entity, props.record, meta) : null)
})
const effectFor = (name) => (name.startsWith('cf.') ? effects.value[`custom_fields.${name.slice(3)}`] : effects.value[name]) || {}

// ---------------------------------------------------------------- layout

const standardNames = computed(() => new Set(meta.fields(props.entity).map((f) => f.name)))
const groups = computed(() => {
  const out = layoutSections(props.entity, meta).map((g) => ({
    key: g.key,
    title: sectionTitle(g, meta, t),
    wide: g.wide,
    fields: g.fields.filter((name) => {
      if (name.startsWith('cf.')) return Boolean(meta.customField(props.entity, name.slice(3))) && effectFor(name).visible !== false
      if (!standardNames.value.has(name) && !SPECIAL_FIELDS.includes(name)) return false
      if (CREATE_ONLY.includes(name) && !creating.value) return false
      if (props.entity === 'lead' && ['pipeline', 'stage'].includes(name) && meta.pipelines('lead').length === 0) return false
      if (props.entity === 'opportunity' && ['pipeline'].includes(name) && meta.pipelines('opportunity').length < 2) return false
      // Once the record exists its stage moves on the stage bar, confirmed there (Dynamics).
      if (name === 'stage' && !creating.value && ['opportunity', 'lead'].includes(props.entity)) return false
      if (name === 'owner' && !creating.value && props.record?.abilities?.assign === false) return false
      if (name === 'is_private' && !session.has(`${props.entity}:view_private`) && !creating.value && !props.record?.is_private) return false
      return fieldApplies(props.entity, name, inputs) && effectFor(name).visible !== false
    }),
  }))
  return out.filter((g) => g.fields.length)
})
const wideField = (name) => {
  if (['contact_points', 'addresses', 'tags', 'product_interests', 'regarding', 'noteable', 'parties', 'body', 'description', 'modules'].includes(name)) return true
  if (props.entity === 'activity' && name === 'start_at') return true
  const f = meta.field(props.entity, name)
  if (f && WIDE_TYPES.includes(f.type)) return true
  const cf = name.startsWith('cf.') ? meta.customField(props.entity, name.slice(3)) : null
  return cf ? ['textarea', 'richtext', 'multiselect'].includes(cf.type) : false
}

// ---------------------------------------------------------------- duplicates while typing (D-10)

const duplicateQuery = computed(() => {
  if (!['account', 'contact', 'lead'].includes(props.entity)) return null
  const q = {}
  const pick = (k) => { if (typeof inputs[k] === 'string' && inputs[k].trim().length >= 3) q[k] = inputs[k].trim() }
  ;({ account: ['name', 'tax_id', 'website'], contact: ['first_name', 'last_name'], lead: ['company_name', 'first_name', 'last_name', 'email', 'phone'] })[props.entity].forEach(pick)
  const points = inputs.contact_points || []
  const emails = points.filter((p) => p.channel === 'email' && p.value.includes('@')).map((p) => p.value)
  const phones = points.filter((p) => p.channel === 'phone' && p.value.replace(/\D/g, '').length >= 6).map((p) => p.value)
  if (emails.length) q.emails = Object.fromEntries(emails.map((e, i) => [i, e]))
  if (phones.length) q.phones = Object.fromEntries(phones.map((e, i) => [i, e]))
  if (!Object.keys(q).length) return null
  if (props.record?.uuid) q.exclude = props.record.uuid
  return q
})
const checkDuplicates = useDebounce(async () => {
  if (!duplicateQuery.value) {
    liveDuplicates.value = []
    return
  }
  try {
    const body = await api(`/${entityPath(props.entity)}/duplicates`, { query: duplicateQuery.value, timeoutMs: 6000 })
    liveDuplicates.value = body.data
  } catch {
    liveDuplicates.value = []
  }
}, 450)
watch(duplicateQuery, () => checkDuplicates(), { deep: true })

// ---------------------------------------------------------------- saving

async function submit({ ackDuplicates = false } = {}) {
  formError.value = ''
  if (dictations.size) {
    dictations.forEach((d) => d.stop())
    await Promise.all([...dictations].map((d) => d.settled()))
  }
  const missing = unmetRequirements(effects.value, inputs)
  const requiredStandard = meta.fields(props.entity).filter((f) => f.required && !f.readonly && groups.value.some((g) => g.fields.includes(f.name)))
  for (const f of requiredStandard) {
    const v = f.type === 'ref' ? inputs[f.input] : inputs[f.name]
    if (v === null || v === undefined || v === '') missing.push(f.type === 'ref' ? f.input : f.name)
  }
  // A value in the wrong form (a rule's pattern, D-339): said under its field in the company's words.
  const misfits = unmetPatterns(effects.value, inputs, props.record, t('messages.patternUnmet'))
  if (missing.length || Object.keys(misfits).length) {
    errors.value = { ...Object.fromEntries(missing.map((k) => [k, t('common.required')])), ...misfits }
    formError.value = t(missing.length ? 'messages.rulesUnmet' : 'messages.patternsUnmet')
    return null
  }
  const body = { ...bodyFrom(props.entity, inputs, original, meta, { creating: creating.value }), ...extended() }
  if (!creating.value && Object.keys(body).length === 0) {
    emit('saved', props.record, { status: 'unchanged' })
    return props.record
  }
  if (ackDuplicates) body.duplicate_ack = true
  saving.value = true
  try {
    const result = await saveRecord(props.entity, props.record?.uuid || null, body, { baseVersion: props.record?.version ?? null, label: inputs.name || inputs.topic || inputs.subject || inputs.title || inputs.last_name || '' })
    if (result.status === 'error' && result.data?.code === 'DUPLICATE_SUSPECTED') {
      duplicates.value = result.data.details?.candidates || []
      duplicateBlocking.value = result.data.details?.can_override === false
      return null
    }
    if (result.status === 'conflict') {
      // Someone saved the same fields meanwhile: the card lets the person choose (D-38).
      emit('conflict', { conflict: result.conflict, body })
      return null
    }
    errors.value = fieldErrors(result)
    report(result, { success: creating.value ? t('messages.created') : t('messages.saved'), quietSuccess: props.quietSuccess })
    if (result.status === 'saved' || result.status === 'queued') {
      duplicates.value = []
      emit('saved', result.record || { uuid: result.uuid, entity: props.entity, ...body }, result)
      return result
    }
    return null
  } finally {
    saving.value = false
  }
}

defineExpose({ submit, dirty, changed, changedCount, snapshot, saving })
</script>

<template>
  <form class="space-y-5" novalidate @submit.prevent="submit()">
    <UiNotice v-if="formError" tone="error">{{ formError }}</UiNotice>

    <UiNotice v-if="duplicates.length" :tone="duplicateBlocking ? 'error' : 'warning'">
      <p class="font-medium">{{ duplicateBlocking ? $t('messages.duplicateBlocked') : $t('messages.duplicateWarning') }}</p>
      <ul class="mt-1 space-y-0.5">
        <li v-for="d in duplicates" :key="d.uuid">
          <router-link class="link" :to="{ name: 'record', params: { entityPath: ENTITIES[d.entity].path, uuid: d.uuid } }" target="_blank">{{ d.label }}</router-link>
          <span class="text-xs opacity-70"> · {{ $t(`entities.${d.entity}.one`) }} · {{ $t('duplicates.score', { n: Math.round(d.score * 100) }) }}</span>
        </li>
      </ul>
      <template v-if="!duplicateBlocking" #action>
        <UiButton size="sm" @click="submit({ ackDuplicates: true })">{{ $t('messages.ackDuplicates') }}</UiButton>
      </template>
    </UiNotice>

    <section v-for="(group, i) in groups" :key="group.key">
      <h3 v-if="group.title" class="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">{{ group.title }}</h3>
      <div :class="['grid gap-x-4 gap-y-3', compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2']">
        <div v-for="name in group.fields" :key="name" :class="!compact && (wideField(name) || group.wide) ? 'sm:col-span-2' : ''">
          <FieldInput :entity="entity" :name="name" :changed="changedNames.has(name)" :printed="printed.includes(name)" :inputs="inputs" :refs="refs" :effect="effectFor(name)" :errors="errors" :record="record" :disabled="readonly" @set="set" @set-ref="(name, r) => (refs[name] = { uuid: r.uuid, label: r.label, entity: r.entity, account: r.account || null, _suggested: !!r._suggested })" />
        </div>
      </div>
      <ActivityReminders v-if="i === 0 && remindable" v-model="inputs.reminders" v-model:remind-me="inputs.remind_me" class="mt-3" :for-someone-else="forSomeoneElse" :all-day="!!inputs.all_day" :disabled="readonly" />
    </section>

    <div v-if="liveDuplicates.length && !duplicates.length" class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
      <p class="mb-1 font-medium"><i class="fa-solid fa-clone mr-1" aria-hidden="true"></i>{{ $t('duplicates.title') }}</p>
      <ul class="space-y-0.5">
        <li v-for="d in liveDuplicates" :key="d.uuid" class="flex items-center gap-2">
          <router-link class="link truncate" :to="{ name: 'record', params: { entityPath: ENTITIES[d.entity].path, uuid: d.uuid } }" target="_blank">{{ d.label }}</router-link>
          <span class="text-xs text-faint">{{ $t(`entities.${d.entity}.one`) }} · {{ $t('duplicates.score', { n: Math.round(d.score * 100) }) }}</span>
        </li>
      </ul>
    </div>

    <div v-if="!hideActions && !readonly" class="flex justify-end gap-2 border-t border-default pt-4">
      <UiButton @click="emit('cancel')">{{ $t('common.cancel') }}</UiButton>
      <UiButton type="submit" variant="primary" :loading="saving" icon="fa-check">{{ submitLabel || (creating ? $t('common.create') : $t('common.save')) }}</UiButton>
    </div>
  </form>
</template>
